import React, { useEffect, useMemo, useState } from 'react';
import { summarize, NotepadRequest, NotepadResponse, listSessions, getSession, saveSession, NotepadSessionListItem, generateVideoOutline, VideoSlide } from '../../services/notepadService';
import { storage } from '../../lib/storage';

const Notepad: React.FC = () => {
  const [text, setText] = useState('');
  const [summary, setSummary] = useState(true);
  const [studyGuide, setStudyGuide] = useState(false);
  const [faq, setFaq] = useState(false);
  const [briefingDoc, setBriefingDoc] = useState(false);
  const [tone, setTone] = useState<'concise' | 'formal' | 'friendly'>('concise');
  const [length, setLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NotepadResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'studyGuide' | 'faq' | 'briefingDoc'>('summary');
  const [sessions, setSessions] = useState<NotepadSessionListItem[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [copiedTab, setCopiedTab] = useState<'summary' | 'studyGuide' | 'faq' | 'briefingDoc' | null>(null);
  const [sources, setSources] = useState<Array<{ title?: string; text: string }>>([]);
  const [includeSources, setIncludeSources] = useState<boolean>(false);
  const [maxSlides, setMaxSlides] = useState<number>(5);
  const [generatingOutline, setGeneratingOutline] = useState<boolean>(false);
  const [videoSlides, setVideoSlides] = useState<VideoSlide[] | null>(null);
  const [creatingVideo, setCreatingVideo] = useState<boolean>(false);
  const [videoMessage, setVideoMessage] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoStorageKey, setVideoStorageKey] = useState<string | null>(null);

  const hasAnyOutput = useMemo(() => !!(result?.summary || result?.studyGuide || result?.faq || (result as any)?.briefingDoc), [result]);
  const charCount = text.length;
  const maxCharsApprox = 15000;

  useEffect(() => {
    // Load recent sessions on mount
    (async () => {
      try {
        const items = await listSessions(20);
        setSessions(items);
      } catch (e) {
        console.error('Failed to load sessions', e);
      }
    })();
  }, []);

  const onLoadSession = async () => {
    if (!selectedSessionId) return;
    setError(null);
    setLoading(true);
    try {
      const s = await getSession(selectedSessionId);
      setText(s.input_text || '');
      setSummary(!!s.options?.summary);
      setStudyGuide(!!s.options?.studyGuide);
      setFaq(!!s.options?.faq);
      setBriefingDoc(!!(s.options as any)?.briefingDoc);
      setTone((s.tone as any) || 'concise');
      setLength((s.length as any) || 'medium');
      setSources(Array.isArray(s.sources) ? (s.sources as any[]).filter(Boolean) as Array<{ title?: string; text: string }> : []);
      setIncludeSources(!!(s as any).includeSources);
      if (s.outputs) {
        setResult(s.outputs);
      } else {
        setResult(null);
      }
      if (s.outputs?.summary) setActiveTab('summary');
      else if (s.outputs?.studyGuide) setActiveTab('studyGuide');
      else if (s.outputs?.faq) setActiveTab('faq');
      else if ((s.outputs as any)?.briefingDoc) setActiveTab('briefingDoc');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load session';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    setError(null);
    setLoading(true);
    try {
      if (!summary && !studyGuide && !faq && !briefingDoc) {
        setError('Please select at least one output option.');
        return;
      }
      const req: NotepadRequest = { text, options: { summary, studyGuide, faq, briefingDoc } as any, tone, length, sources, includeSources };
      const data = await summarize(req);
      setResult(data);
      // Set tab to first available
      if (data.summary) setActiveTab('summary');
      else if (data.studyGuide) setActiveTab('studyGuide');
      else if (data.faq) setActiveTab('faq');
      else if ((data as any).briefingDoc) setActiveTab('briefingDoc');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Notepad</h1>
        <p className="text-sm text-gray-600">Paste text and generate a Summary, Study Guide, and/or FAQ.</p>
      </div>

      {/* Session controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-end md:space-x-3 space-y-3 md:space-y-0">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Previous sessions</label>
            <div className="relative">
              <select
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
              >
                <option value="">Select a session…</option>
                {sessions.map(s => (
                  <option key={s.id} value={s.id}>
                    {(s.title || 'Untitled') + ' — ' + new Date(s.updated_at).toLocaleString()}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">▾</div>
            </div>

            {/* Video Outline UI removed per requirements; video generation is single-click from Actions below */}
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition disabled:opacity-60"
            onClick={onLoadSession}
            disabled={!selectedSessionId || loading}
          >
            Load session
          </button>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Input and options */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Paste your text</label>
            <div className="relative">
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 h-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`Paste up to ~${maxCharsApprox.toLocaleString()} characters here...`}
              />
              <div className="absolute bottom-2 right-3 text-xs text-gray-500">
                {charCount.toLocaleString()} / ~{maxCharsApprox.toLocaleString()}
              </div>
            </div>

            {/* Sources */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Sources</label>
                <button
                  type="button"
                  className="text-sm px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                  onClick={() => setSources(s => [...s, { title: '', text: '' }])}
                >
                  Add source
                </button>
              </div>
              {sources.length === 0 && (
                <p className="text-xs text-gray-500">Optionally add additional sources to incorporate into the summary.</p>
              )}
              <div className="space-y-4">
                {sources.map((s, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <input
                        type="text"
                        className="w-full mr-2 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Source title (optional)"
                        value={s.title || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSources(arr => arr.map((it, i) => i === idx ? { ...it, title: v } : it));
                        }}
                      />
                      <button
                        type="button"
                        className="ml-2 text-sm px-2 py-1 rounded border border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
                        onClick={() => setSources(arr => arr.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg p-3 h-28 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Paste source text"
                      value={s.text}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSources(arr => arr.map((it, i) => i === idx ? { ...it, text: v } : it));
                      }}
                    />
                  </div>
                ))}
              </div>
              <label className="mt-3 inline-flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={includeSources}
                  onChange={(e) => setIncludeSources(e.target.checked)}
                />
                <span className="text-sm text-gray-700">Include sources in summary</span>
              </label>
            </div>

            {/* Output toggles */}
            <div className="mt-5">
              <span className="block text-sm font-medium text-gray-700 mb-2">Select outputs</span>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => setSummary(v => !v)}
                  className={`rounded-lg border px-3 py-2 text-sm transition ${summary ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => setStudyGuide(v => !v)}
                  className={`rounded-lg border px-3 py-2 text-sm transition ${studyGuide ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Study Guide
                </button>
                <button
                  type="button"
                  onClick={() => setFaq(v => !v)}
                  className={`rounded-lg border px-3 py-2 text-sm transition ${faq ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  FAQ
                </button>
                <button
                  type="button"
                  onClick={() => setBriefingDoc(v => !v)}
                  className={`rounded-lg border px-3 py-2 text-sm transition ${briefingDoc ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Briefing Document
                </button>
              </div>
            </div>

            {/* Tone & Length */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={tone}
                  onChange={(e) => setTone(e.target.value as any)}
                >
                  <option value="concise">Concise</option>
                  <option value="formal">Formal</option>
                  <option value="friendly">Friendly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={length}
                  onChange={(e) => setLength(e.target.value as any)}
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>
            )}

            {/* Actions */}
            <div className="mt-5 flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setText('')}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition"
                disabled={loading}
              >
                Clear
              </button>
              <button
                onClick={onSubmit as any}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
                disabled={loading}
              >
                {loading ? 'Generating…' : 'Generate'}
              </button>
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-60"
                disabled={creatingVideo || !text.trim()}
                onClick={async () => {
                  if (!text.trim()) return;
                  setError(null);
                  setVideoMessage(null);
                  setVideoUrl(null);
                  setVideoStorageKey(null);
                  setCreatingVideo(true);
                  try {
                    const { generateVideoOutline, createVoiceover, renderVideo, uploadSlidesData } = await import('../../services/notepadService');
                    const { renderSlidesClientData } = await import('../../utils/slideRenderer');
                    // Ensure outline (not shown to user)
                    let slidesToUse = videoSlides;
                    if (!slidesToUse || slidesToUse.length === 0) {
                      setVideoMessage('Generating outline…');
                      const { slides } = await generateVideoOutline({ text, tone, sources, includeSources, maxSlides });
                      slidesToUse = slides;
                      setVideoSlides(slides);
                    }
                    // Prepend a title slide for a short intro. Title uses first slide title or 'Overview'.
                    const introTitle = slidesToUse && slidesToUse[0]?.title ? slidesToUse[0].title : 'Overview';
                    const introSlide = { title: introTitle, bullets: [], narration: '' } as any;
                    slidesToUse = [introSlide, ...slidesToUse!];
                    // Render slides client-side to data URLs, then upload via backend (server-side Supabase)
                    setVideoMessage('Rendering slides…');
                    let images: Array<{ url: string }>|undefined;
                    try {
                      const renderedData = await renderSlidesClientData(slidesToUse!.map(s => ({ title: s.title, bullets: s.bullets, narration: s.narration })));
                      setVideoMessage('Uploading slides…');
                      const uploaded = await uploadSlidesData({ dataUrls: renderedData.dataUrls });
                      images = uploaded.images;
                    } catch (e:any) {
                      console.error('Client slide render/upload failed:', e);
                      setCreatingVideo(false);
                      setVideoMessage('Failed to render slides. Please try again.');
                      setError(e?.message || 'Failed to render slides');
                      return;
                    }
                    if (!images || images.length !== slidesToUse!.length) {
                      setVideoMessage('Failed to render slides. Please try again.');
                      return;
                    }
                    // Voiceover
                    setVideoMessage('Creating voiceover…');
                    const vo = await createVoiceover({ slides: slidesToUse! });
                    // Render
                    setVideoMessage('Rendering video…');
                    let rv: { videoUrl?: string } | undefined;
                    // Try render with a few retries if backend reports images not ready
                    for (let attempt = 1; attempt <= 10; attempt++) {
                      try {
                        rv = await renderVideo({ slides: slidesToUse!, audio: vo.audio.map(a => ({ url: a.url, durationSec: a.durationSec })), images });
                        break;
                      } catch (e: any) {
                        const msg = String(e?.message || e || 'Render failed');
                        if (msg.includes('409') || msg.toLowerCase().includes('images not ready')) {
                          setVideoMessage(`Images are finalizing… retrying render (${attempt}/10)…`);
                          await new Promise(res => setTimeout(res, 10_000));
                          continue;
                        }
                        throw e;
                      }
                    }
                    if (rv?.videoUrl) {
                      setVideoMessage('Finalizing video…');
                      try {
                        const response = await fetch(rv.videoUrl, { mode: 'cors' });
                        if (!response.ok) {
                          throw new Error(`Video download failed (${response.status})`);
                        }
                        const videoBlob = await response.blob();
                        if (!videoBlob || videoBlob.size === 0) {
                          throw new Error('Rendered video blob was empty');
                        }
                        const extension = rv.videoUrl.split('?')[0].split('.').pop() || 'mp4';
                        const fileName = `notepad-video-${Date.now()}.${extension}`;
                        const file = new File([videoBlob], fileName, { type: videoBlob.type || 'video/mp4' });
                        const upload = await storage.uploadFile({
                          file,
                          path: `notepad/videos/${fileName}`,
                          metadata: {
                            sourceModule: 'notepad-video',
                            docType: 'video',
                            visibility: 'private',
                            tags: [
                              'notepad-video',
                              `tone:${tone}`,
                              `length:${length}`,
                              includeSources ? 'includes-sources' : null,
                            ].filter(Boolean) as string[],
                            promptLength: text.length,
                            slidesCount: slidesToUse?.length || 0,
                            originalUrl: rv.videoUrl,
                          },
                        });
                        const signedUrl = await storage.getSignedUrl(upload.key, 600);
                        setVideoStorageKey(upload.key);
                        setVideoUrl(signedUrl);
                        setVideoMessage('Video ready');
                      } catch (assetError) {
                        console.error('Failed to persist notepad video to storage:', assetError);
                        setVideoUrl(rv.videoUrl);
                        setVideoMessage('Video ready (using transient URL)');
                      }
                    } else {
                      setVideoMessage('Render completed, but no video URL was returned.');
                    }
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : 'Failed to create video';
                    setVideoMessage(msg);
                  } finally {
                    setCreatingVideo(false);
                  }
                }}
              >
                {creatingVideo ? 'Working…' : 'Create Video'}
              </button>
            </div>
            {videoMessage && (
              <div className="mt-2 text-xs text-gray-700 break-all flex items-center gap-3 flex-wrap">
                <span>{videoMessage}</span>
                {videoUrl && (
                  <>
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs"
                    >
                      Download Video
                    </a>
                    <button
                      type="button"
                      className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs"
                      onClick={() => navigator.clipboard.writeText(videoUrl)}
                    >
                      Copy Link
                    </button>
                    {videoStorageKey && (
                      <span className="text-[10px] text-gray-400">
                        Stored as <code>{videoStorageKey}</code>
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-6 h-full flex flex-col">
            {!result && !loading && (
              <div className="flex-1 flex items-center justify-center text-center text-gray-500">
                <div>
                  <div className="text-lg font-medium mb-1">No results yet</div>
                  <div className="text-sm">Paste your text on the left and click Generate to see results here.</div>
                </div>
              </div>
            )}

            {loading && (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-3 bg-gray-200 rounded" />
                <div className="h-3 bg-gray-200 rounded w-5/6" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            )}

            {result && (
              <>
                {/* Tabs */}
                <div className="mb-4">
                  <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                    {result.summary && (
                      <button
                        className={`px-4 py-2 text-sm ${activeTab === 'summary' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => setActiveTab('summary')}
                      >
                        Summary
                      </button>
                    )}
                    {result.studyGuide && (
                      <button
                        className={`px-4 py-2 text-sm border-l border-gray-200 ${activeTab === 'studyGuide' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => setActiveTab('studyGuide')}
                      >
                        Study Guide
                      </button>
                    )}
                    {result.faq && (
                      <button
                        className={`px-4 py-2 text-sm border-l border-gray-200 ${activeTab === 'faq' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => setActiveTab('faq')}
                      >
                        FAQ
                      </button>
                    )}
                    {(result as any).briefingDoc && (
                      <button
                        className={`px-4 py-2 text-sm border-l border-gray-200 ${activeTab === 'briefingDoc' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => setActiveTab('briefingDoc')}
                      >
                        Briefing Doc
                      </button>
                    )}
                  </div>
                </div>

                {/* Tab content with copy button */}
                {activeTab === 'summary' && result.summary && (
                  <div className="relative">
                    <button
                      type="button"
                      className="absolute top-0 right-0 text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                      onClick={() => {
                        navigator.clipboard.writeText(result.summary || '');
                        setCopiedTab('summary');
                        setTimeout(() => setCopiedTab(null), 1200);
                      }}
                    >
                      {copiedTab === 'summary' ? 'Copied' : 'Copy'}
                    </button>
                    <pre className="whitespace-pre-wrap text-sm leading-6 bg-gray-50 border border-gray-200 rounded-lg p-4">{result.summary}</pre>
                  </div>
                )}
                {activeTab === 'studyGuide' && result.studyGuide && (
                  <div className="relative">
                    <button
                      type="button"
                      className="absolute top-0 right-0 text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                      onClick={() => {
                        navigator.clipboard.writeText(result.studyGuide || '');
                        setCopiedTab('studyGuide');
                        setTimeout(() => setCopiedTab(null), 1200);
                      }}
                    >
                      {copiedTab === 'studyGuide' ? 'Copied' : 'Copy'}
                    </button>
                    <pre className="whitespace-pre-wrap text-sm leading-6 bg-gray-50 border border-gray-200 rounded-lg p-4">{result.studyGuide}</pre>
                  </div>
                )}
                {activeTab === 'faq' && result.faq && (
                  <div className="relative">
                    <button
                      type="button"
                      className="absolute top-0 right-0 text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                      onClick={() => {
                        navigator.clipboard.writeText(result.faq || '');
                        setCopiedTab('faq');
                        setTimeout(() => setCopiedTab(null), 1200);
                      }}
                    >
                      {copiedTab === 'faq' ? 'Copied' : 'Copy'}
                    </button>
                    <pre className="whitespace-pre-wrap text-sm leading-6 bg-gray-50 border border-gray-200 rounded-lg p-4">{result.faq}</pre>
                  </div>
                )}

                {activeTab === 'briefingDoc' && (result as any)?.briefingDoc && (
                  <div className="relative">
                    <button
                      type="button"
                      className="absolute top-0 right-0 text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                      onClick={() => {
                        navigator.clipboard.writeText((result as any).briefingDoc || '');
                        setCopiedTab('briefingDoc');
                        setTimeout(() => setCopiedTab(null), 1200);
                      }}
                    >
                      {copiedTab === 'briefingDoc' ? 'Copied' : 'Copy'}
                    </button>
                    <pre className="whitespace-pre-wrap text-sm leading-6 bg-gray-50 border border-gray-200 rounded-lg p-4">{(result as any).briefingDoc}</pre>
                  </div>
                )}

                {/* Save Session */}
                <div className="mt-6 border-t pt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                    <input
                      type="text"
                      className="border border-gray-300 rounded-lg px-3 py-2 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional title (e.g., Chapter 3 notes)"
                      value={sessionTitle}
                      onChange={(e) => setSessionTitle(e.target.value)}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-60"
                      disabled={!hasAnyOutput || saving}
                      onClick={async () => {
                        if (!hasAnyOutput) return;
                        setError(null);
                        setSaving(true);
                        try {
                          const { sessionId } = await saveSession({
                            title: sessionTitle.trim() || undefined,
                            text,
                            options: { summary, studyGuide, faq, briefingDoc } as any,
                            tone,
                            length,
                            outputs: result || undefined,
                            sources,
                            includeSources,
                          });
                          // Refresh list
                          const items = await listSessions(20);
                          setSessions(items);
                          setSelectedSessionId(sessionId);
                        } catch (err) {
                          const msg = err instanceof Error ? err.message : 'Failed to save session';
                          setError(msg);
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      {saving ? 'Saving…' : 'Save Session'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notepad;
